import { HttpCancelError } from '@cian/peperrors/shared';
import { pathOr, equals } from 'ramda';

import { TThunkAction } from '../../types/redux';
import { EErrorNotificationType } from '../../types/notifications';
import { getVisibleTiles, tileToBbox } from '../../utils/tiles';
import { zoomToPrecision, precisionToMinZoom } from '../../utils/precision';
import { combineBboxes } from '../../utils/geometry';
import { actionGenerator } from '../../utils/redux/actionGenerator';
import { IBBoxSchema } from '../../repositories/search-engine/entities/bbox/BBoxSchema';
import { fetchClusters as requestFetchClusters } from '../../services/clusters';
import { prepareClusters } from '../../mappers/clusters/prepareClusters';
import { pushErrorNotification } from '../notifications';
import { EResultsActionType, IFetchClustersSucceedParameters } from './types';
import { getRequiredTiles, assertJsonQueryValidity } from './helpers';
import { ITilesMap } from '../../types/map';

const setStatusLoading = actionGenerator<EResultsActionType.Loading>(EResultsActionType.Loading);
const setStatusSucceed = actionGenerator<EResultsActionType.Succeed, IFetchClustersSucceedParameters>(
  EResultsActionType.Succeed,
);
const setStatusFailed = actionGenerator<EResultsActionType.Failed>(EResultsActionType.Failed);

interface IFetchClustersParameters {
  updateBounds?: boolean;
}

export function fetchClusters({ updateBounds = false }: IFetchClustersParameters = {}): TThunkAction<Promise<void>> {
  return async (dispatch, getState, context) => {
    const {
      config,
      custom: { subdomain },
      logger,
    } = context;
    const {
      features,
      filters: { jsonQuery },
      mapBounds,
      results: { jsonQuery: resultsJsonQuery },
    } = getState();

    const { bounds, zoom: currentZoom } = mapBounds;
    if (!bounds || !currentZoom) {
      logger.error('Either bounds or zoom is undefined', { domain: 'actions/results/fetchClusters' });

      return;
    }

    const clean = !equals(jsonQuery, resultsJsonQuery);

    const precision = zoomToPrecision(config, currentZoom);
    const zoom = precisionToMinZoom(config, precision);
    const visibleTiles = getVisibleTiles(bounds, zoom);

    let requiredTiles = visibleTiles;
    if (!clean) {
      const loadedTiles = pathOr<ITilesMap>({}, [precision, 'tiles'], features);

      requiredTiles = getRequiredTiles(loadedTiles, visibleTiles);
      if (requiredTiles.length === 0) {
        logger.debug('Unfortunately we tried to fetch already loaded tiles', {
          domain: 'actions/results/fetchClusters',
        });

        return;
      }
    }

    dispatch(setStatusLoading());

    try {
      const requiredBboxes = requiredTiles.map(t => tileToBbox(t, zoom));
      const combinedBbox = combineBboxes(requiredBboxes);

      const clustersData = await requestFetchClusters(context, {
        subdomain,
        params: {
          zoom: updateBounds ? undefined : zoom,
          bbox: updateBounds ? undefined : bboxToApiSchema(combinedBbox),
          jsonQuery,
          extended: !updateBounds,
        },
      });

      const clusters = prepareClusters(clustersData, mapBounds);

      assertJsonQueryValidity(logger, jsonQuery, clusters.jsonQuery);

      dispatch(
        setStatusSucceed({
          ...clusters,
          jsonQuery,
          clean,
          tiles: requiredTiles,
          updateBounds,
        }),
      );
    } catch (error) {
      if (error instanceof HttpCancelError) {
        return;
      }

      logger.error(error);

      dispatch(setStatusFailed());
      dispatch(pushErrorNotification(EErrorNotificationType.FetchClusters));
    }
  };
}

function bboxToApiSchema(coords: [YMaps.TCoord, YMaps.TCoord]): IBBoxSchema[] {
  const [[latMin, lngMin], [latMax, lngMax]] = coords;

  return [
    {
      bottomRight: { lat: latMin, lng: lngMax },
      topLeft: { lat: latMax, lng: lngMin },
    },
  ];
}
